import React from "react";
import {
  CardContent,
  Typography,
  IconButton,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { castEnumForDisplay } from "../../helpers/enumHelpers";
import { StyledCard } from "../common/StyledCard";
import { RoleResponseDto } from "../../shared/dtos/role/RoleResponseDto";

interface RoleCardProps {
  role: RoleResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const RoleCard: React.FC<RoleCardProps> = ({ role, onEdit, onDelete }) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        {/* Top Section */}
        <Box>
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: "bold" }}
            >
              {castEnumForDisplay(role.name)}
            </Typography>
            <Stack direction="row">
              {onEdit && (
                <Tooltip title="Edit Role & Permissions">
                  <IconButton onClick={onEdit} size="small" color="secondary">
                    <EditOutlinedIcon />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Delete Role">
                  <IconButton onClick={onDelete} size="small" color="error">
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {role.description}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Bottom Section */}
        <Box sx={{ mt: 2 }}>
          <Accordion
            sx={{ boxShadow: "none", "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              sx={{ minHeight: 40, "&.Mui-expanded": { minHeight: 40 } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Permissions ({role.permissions?.length || 0})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ maxHeight: 200, overflowY: "auto", p: 1 }}>
              {role.permissions && role.permissions.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {role.permissions.map((permission: string) => (
                    <Chip
                      key={permission}
                      label={permission}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No permissions assigned.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default RoleCard;
